// MobileEC — phone view for the Engineer Challenge schedule.
//
// Simple stacked list — each EC run shows the run code + time + team
// chip + status pill. EC runs on the 1v1 field, so the clock chip
// reads the 1v1 delay. Ops can tap the status pill to edit (same path
// as Match Control / 3v3 / 1v1 schedules — SET_MATCH_STATUS).

import type { Dispatch } from 'react';
import type { Action, AppRole, AppState, MatchTeamStatus, NavigateFn } from '../../types';
import { ProtoClockChip, ProtoEditableStatusPill, EDITABLE_STATUSES, VOL_EDITABLE_STATUSES, ProtoTeamChip, ProtoMatchCode } from '../../components';
import { MobileTopbar } from '../../components/MobileTopbar';
import { teamHasInspectionProgress } from '../../state/inspection';

export function MobileEC({ state, onMenu, onNavigate, role, dispatch }: {
  state: AppState;
  onMenu: () => void;
  onNavigate?: NavigateFn;
  role?: AppRole;
  dispatch?: Dispatch<Action>;
}) {
  const isOps = role === 'ops';
  const isVolPlus = role === 'volunteer' || role === 'staff' || isOps;
  const editableStatusesForRole = isOps ? EDITABLE_STATUSES : VOL_EDITABLE_STATUSES;
  const editECStatus = (matchId: string) => (status: MatchTeamStatus) => {
    dispatch?.({ type: 'SET_MATCH_STATUS', event: 'ec', matchId, side: 'red', status });
  };
  // Volunteers (only) are locked out of at_inspection → not_tracked
  // once S1 is cleared. Staff + ops bypass the lockdown.
  const canVolEditEC = (matchId: string, teamId: string, currentStatus: MatchTeamStatus): boolean => {
    if (!isVolPlus) return false;
    if (role !== 'volunteer') return true;
    if (currentStatus !== 'at_inspection') return true;
    return !teamHasInspectionProgress(teamId, matchId, state);
  };
  return (
    <div className="mw-app">
      <MobileTopbar
        title="ENGINEER"
        subtitle={`JUN 30 · 1V1 FIELD · ${state.ec.length} RUNS`}
        onMenu={onMenu}
        right={<ProtoClockChip delayMin={state.officialDelay['1v1'] ?? 0} hideTime />}
      />
      <div className="mw-body">
        <div className="mw-list-head">
          <span>RUNS</span>
          <span>{state.ec.filter((e) => e.completed).length} COMPLETED</span>
        </div>
        {state.ec.map((e) => {
          const engineerLevel = state.teamRobotInspection[e.team]?.['ec']?.['Engineer'] ?? 'pending';
          const inspection = { passed: engineerLevel === 'l3' ? 1 : 0, total: 1 };
          return (
            <div key={e.id} className={`mw-mrow ${e.live ? 'live' : ''}`}>
              <div className="mw-mrow-head">
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
                  <ProtoMatchCode code={e.id} live={e.live} />
                  <span className="mono tnum" style={{ fontSize: 11, color: 'var(--text-1)' }}>{e.day} · {e.time}</span>
                </div>
                {e.live ? (
                  <span className="mono" style={{ fontSize: 9, color: 'var(--accent)', letterSpacing: '0.12em', fontWeight: 700 }}>● LIVE</span>
                ) : null}
              </div>
              <div className="mw-mrow-body">
                <div className="mw-mrow-side">
                  <div className="swatch" />
                  <span className="name">
                    <ProtoTeamChip
                      id={e.team}
                      logo
                      onClick={onNavigate ? () => onNavigate('teams', { team: e.team }) : undefined}
                    />
                  </span>
                  <ProtoEditableStatusPill
                    status={e.status}
                    onChange={canVolEditEC(e.id, e.team, e.status) ? editECStatus(e.id) : undefined}
                    editableStatuses={editableStatusesForRole}
                    inspection={inspection}
                  />
                </div>
              </div>
            </div>
          );
        })}
        <div className="mw-tail-spacer" />
      </div>
    </div>
  );
}
